/**
 * Shared job-form validation for PostJobPage and EditJobPage (Prompt #472).
 *
 * Mirrors functions/src/utils/job-validation.ts (the server is authoritative;
 * this is UX only): same bounds, same America/Chicago "today", real-date
 * comparisons, and the same split between FORMAT checks (always, drafts too)
 * and COMPLETENESS checks (publishing only).
 */

export const JOB_TIME_ZONE = 'America/Chicago';
export const SALARY_MAX = 1000000;
export const POSITIONS_MIN = 1;
export const POSITIONS_MAX = 999;
export const START_DATE_MAX_MONTHS = 6;
export const TITLE_MIN_PUBLISH = 5;
export const DESCRIPTION_MIN_PUBLISH = 10;
export const REQUIREMENTS_MIN_PUBLISH = 10;
export const DURATION_UNITS = ['Days', 'Weeks', 'Months', 'Years'];
export const EMPLOYMENT_TYPES = ['Full-time', 'Part-time', 'Contract', 'Casual'];

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Days since epoch for a strict, real YYYY-MM-DD date (4-digit year); null otherwise. */
export function parseIsoDate(value) {
  if (typeof value !== 'string') return null;
  const m = ISO_DATE.exec(value);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (mo < 1 || mo > 12 || d < 1) return null;
  const ms = Date.UTC(y, mo - 1, d);
  const back = new Date(ms);
  if (back.getUTCFullYear() !== y || back.getUTCMonth() !== mo - 1 || back.getUTCDate() !== d) return null;
  return Math.round(ms / 86400000);
}

/** Today's date in America/Chicago as YYYY-MM-DD (matches the server, not the browser's zone). */
export function todayInJobTimeZone(now = new Date()) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: JOB_TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(now);
}

/** YYYY-MM-DD plus N calendar months, clamped to the target month's last day. */
export function addMonthsIso(iso, months) {
  const m = ISO_DATE.exec(iso);
  if (!m) return iso;
  const mo = Number(m[2]) - 1 + months;
  const ty = Number(m[1]) + Math.floor(mo / 12);
  const tm = ((mo % 12) + 12) % 12;
  const lastDay = new Date(Date.UTC(ty, tm + 1, 0)).getUTCDate();
  const pad = (n) => String(n).padStart(2, '0');
  return `${ty}-${pad(tm + 1)}-${pad(Math.min(Number(m[3]), lastDay))}`;
}

/** Latest allowed start date (the date input's `max`). */
export function maxStartDate(today = todayInJobTimeZone()) {
  return addMonthsIso(today, START_DATE_MAX_MONTHS);
}

const hasAtMost2Decimals = (v) => Math.abs(v * 100 - Math.round(v * 100)) < 1e-6;
const lines = (text) => String(text || '').split('\n').map((l) => l.trim()).filter(Boolean);

function checkPay(raw, label) {
  if (raw === '' || raw === null || raw === undefined) return null;
  const v = Number(raw);
  if (!Number.isFinite(v)) return `${label} must be a number.`;
  if (v <= 0) return `${label} must be greater than $0.`;
  if (v > SALARY_MAX) return `${label} must be at most $1,000,000.`;
  if (!hasAtMost2Decimals(v)) return `${label} can have at most 2 decimal places.`;
  return null;
}

/**
 * Validate the job form's raw values. Returns { fieldName: message }.
 *
 * opts:
 *  - isDraft: true for Save Draft (format checks only)
 *  - mode: 'create' | 'edit'
 *  - originalStartDate / originalStatus: edit only; the start-date window is
 *    re-checked only when startDate changed, or a draft is being published.
 *  - noEnabledCities: the chosen state has no enabled cities
 *  - today: injectable for tests
 */
export function validateJobForm(form, opts = {}) {
  const { isDraft = false, mode = 'create', originalStartDate = '', originalStatus = '', noEnabledCities = false } = opts;
  const today = opts.today || todayInJobTimeZone();
  const errs = {};
  const publishing = !isDraft;

  // title: required always; >= 5 to publish
  const title = String(form.title || '').trim();
  if (!title) errs.title = 'Job title is required.';
  else if (publishing && title.length < TITLE_MIN_PUBLISH) errs.title = `Job title must be at least ${TITLE_MIN_PUBLISH} characters.`;

  // positions: integer 1..999 always (no silent rewrite)
  const positions = Number(form.positions);
  if (form.positions === '' || form.positions === null || form.positions === undefined || !Number.isInteger(positions)) {
    errs.positions = 'Enter a whole number of workers.';
  } else if (positions < POSITIONS_MIN || positions > POSITIONS_MAX) {
    errs.positions = `Number of workers must be between ${POSITIONS_MIN} and ${POSITIONS_MAX}.`;
  }

  // pay: optional; format always
  const minErr = checkPay(form.salaryMin, 'Min pay');
  const maxErr = checkPay(form.salaryMax, 'Max pay');
  if (minErr) errs.salaryMin = minErr;
  if (maxErr) errs.salaryMax = maxErr;
  if (!minErr && !maxErr && form.salaryMin !== '' && form.salaryMax !== '' &&
      form.salaryMin != null && form.salaryMax != null && Number(form.salaryMax) < Number(form.salaryMin)) {
    errs.salaryMax = 'Max pay must be greater than or equal to min pay.';
  }

  // duration: optional integer >= 1
  if (form.jobDurationValue !== '' && form.jobDurationValue != null) {
    const d = Number(form.jobDurationValue);
    if (!Number.isInteger(d) || d < 1) errs.jobDurationValue = 'Duration must be a whole number of at least 1.';
  }

  // dates: real dates always; window and requirements when publishing
  const isFullTime = form.type === 'Full-time';
  const start = form.startDate ? parseIsoDate(form.startDate) : null;
  const end = !isFullTime && form.endDate ? parseIsoDate(form.endDate) : null;
  if (form.startDate && start === null) errs.startDate = 'Enter a valid start date.';
  if (!isFullTime && form.endDate && end === null) errs.endDate = 'Enter a valid end date.';
  if (start !== null && end !== null && end < start) errs.endDate = 'End date must be on or after the start date.';

  if (publishing) {
    if (!form.startDate) errs.startDate = 'Start date is required.';
    else if (start !== null) {
      const startChanged = mode === 'create' || form.startDate !== originalStartDate || originalStatus === 'draft';
      if (startChanged) {
        if (start < parseIsoDate(today)) errs.startDate = 'Start date must be today or in the future.';
        else if (start > parseIsoDate(maxStartDate(today))) errs.startDate = `Start date must be within ${START_DATE_MAX_MONTHS} months.`;
      }
    }
    // Prompt #474 (Tammy): type is REQUIRED to publish; a missing type is never
    // inferred as non-Full-time, so endDate is required only for a present non-Full-time type.
    if (!form.type) errs.type = 'Employment type is required.';
    else if (!isFullTime && !form.endDate) errs.endDate = 'End date is required.';

    const description = String(form.description || '').trim();
    if (!description) errs.description = 'Job description is required.';
    else if (description.length < DESCRIPTION_MIN_PUBLISH) errs.description = `Description must be at least ${DESCRIPTION_MIN_PUBLISH} characters.`;

    if (!form.state) errs.state = 'State is required.';
    if (noEnabledCities) errs.city = 'No cities are currently available in this state.';
    else if (!form.city) errs.city = 'City is required.';

    const reqs = lines(form.requirements);
    if (reqs.length === 0) errs.requirements = 'Requirements are required.';
    else if (reqs.join('\n').length < REQUIREMENTS_MIN_PUBLISH) errs.requirements = `Requirements must be at least ${REQUIREMENTS_MIN_PUBLISH} characters.`;

    // Prompt #486 (Tammy, 2026-10-02): Benefits is OPTIONAL to publish (like
    // pay). Blank lines are dropped when the job is saved; an empty list is valid.
  }

  return errs;
}

const SERVER_MESSAGES = {
  REQUIRED: 'This field is required.',
  TOO_SHORT: 'This is too short.',
  INVALID_TYPE: 'This value is not valid.',
  INVALID_VALUE: 'This value is not allowed.',
  INVALID_DATE: 'Enter a valid date.',
  IN_PAST: 'Start date must be today or in the future.',
  TOO_FAR: `Start date must be within ${START_DATE_MAX_MONTHS} months.`,
  BEFORE_START: 'End date must be on or after the start date.',
  NOT_ALLOWED_FULL_TIME: 'A Full-time job has no end date.',
  NOT_POSITIVE: 'Must be greater than $0.',
  TOO_LARGE: 'Must be at most $1,000,000.',
  TOO_MANY_DECIMALS: 'At most 2 decimal places.',
  LESS_THAN_MIN: 'Max pay must be greater than or equal to min pay.',
  OUT_OF_RANGE: 'This number is out of range.',
  NOT_ENABLED: 'This city is not currently available.',
  TRANSITION_NOT_ALLOWED: 'This status change is not allowed.',
};

/**
 * Turn a createJob/updateJob invalid-argument error into per-field messages.
 * Returns {} for any other error (the caller shows its generic message).
 */
export function fieldErrorsFromServer(err) {
  const details = err && err.details;
  if (!details || (!Array.isArray(details.issues) && !details.field)) return {};
  const issues = Array.isArray(details.issues) ? details.issues : [{ field: details.field, code: details.code }];
  const out = {};
  for (const { field, code } of issues) {
    if (field && !out[field]) out[field] = SERVER_MESSAGES[code] || 'This value is not valid.';
  }
  return out;
}
